// modules/Menu/MenuNavigation.js
// 菜单导航模块

import GameStateStore from '../GameStateStore.js';

export default class MenuNavigation {
    constructor(controller) {
        this.controller = controller;
    }
    
    /**
     * 显示菜单
     * @param {string} menuType - 菜单类型
     * @param {Object} playerData - 玩家数据
     */
    showMenu(menuType, playerData) {
        this.controller.state.currentMenu = menuType;
        this.controller.state.selectedOption = 0;
        if (menuType === 'equipment') {
            this.controller.state.equipmentInventoryIndex = 0;
        }

        // 更新菜单标题
        this.controller.menuUI.updateTitle(menuType);

        // 装备菜单使用特殊布局
        if (menuType === 'equipment') {
            this.showEquipmentMenu(playerData);
            return;
        }

        // 显示普通菜单
        let options = this.controller.state.menuOptions[menuType];
        
        // 对于非主菜单，在末尾添加"返回"选项
        if (menuType !== 'main' && menuType !== 'team') {
            // 创建带返回选项的列表
            const optionsWithReturn = [...options];
            // 添加返回选项（使用特殊标记以便识别）
            optionsWithReturn.push({ name: '返回', isReturn: true, label: '返回' });
            options = optionsWithReturn;
        }
        
        this.controller.menuUI.showMenu(
            menuType,
            options,
            this.controller.state.selectedOption,
            (index) => this.controller.operation.executeSelectedOption(playerData, options),
            (index) => {
                this.controller.state.selectedOption = index;
                this.controller.operation.updateSelectedOption(playerData);
            },
            this.controller
        );

        // 显示初始信息
        this.controller.operation.updateInfoPanel(playerData);
    }
    
    /**
     * 显示装备菜单
     */
    showEquipmentMenu(playerData) {
        this.controller.equipmentMenuUI.showEquipmentMenu(
            playerData,
            this.controller.state.selectedOption,
            this.controller.state.equipmentInventoryIndex,
            (slotIndex) => {
                this.controller.state.selectedOption = slotIndex;
                this.controller.state.equipmentInventoryIndex = 0;
                this.controller.operation.updateEquipmentMenuSelection(playerData);
            },
            (invIndex) => {
                this.controller.state.equipmentInventoryIndex = invIndex;
                this.controller.operation.updateEquipmentMenuSelection(playerData);
            },
            (invIndex) => {
                this.controller.state.equipmentInventoryIndex = invIndex;
                this.controller.operation.executeEquipmentAction(playerData);
            }
        );
    }

    /**
     * 装备槽方向导航
     */
    handleEquipmentSlotNav(direction) {
        const playerData = GameStateStore.getPlayer();
        const next = this.controller.equipmentHandler.navigateSlot(
            this.controller.state.selectedOption,
            direction
        );
        if (next !== this.controller.state.selectedOption) {
            this.controller.state.selectedOption = next;
            this.controller.state.equipmentInventoryIndex = 0;
            this.controller.operation.updateEquipmentMenuSelection(playerData);
        }
    }

    /**
     * 底部背包装备列表导航（Q/E）
     */
    handleEquipmentInventoryNav(delta) {
        const playerData = GameStateStore.getPlayer();
        const next = this.controller.equipmentHandler.navigateInventory(
            this.controller.state.equipmentInventoryIndex,
            delta,
            this.controller.state.selectedOption,
            playerData
        );
        if (next !== this.controller.state.equipmentInventoryIndex) {
            this.controller.state.equipmentInventoryIndex = next;
            this.controller.operation.updateEquipmentMenuSelection(playerData);
        }
    }
    
    /**
     * 处理主菜单选项
     */
    handleMainMenuOption() {
        const mainOption = this.controller.state.menuOptions.main[this.controller.state.selectedOption];
        switch(mainOption) {
            case '道具':
                this.showMenu('items', GameStateStore.getPlayer());
                break;
            case '装备':
                this.showMenu('equipment', GameStateStore.getPlayer());
                break;
            case '状态':
                this.showMenu('status', GameStateStore.getPlayer());
                break;
            case '战车管理':
                this.openVehicleEquipmentMenu();
                break;
            case '组队':
                this.controller.operation.updateTeamMenu();
                break;
            case '退出':
                this.controller.scene.closeMenu();
                break;
        }
    }
    
    /**
     * 打开战车装备管理菜单
     */
    openVehicleEquipmentMenu() {
        const previousSceneKey = this.controller.scene.previousSceneKey || 'WorldScene';
        
        // 暂停菜单场景
        this.controller.scene.scene.pause('MenuScene');
        
        // 启动战车装备管理场景
        this.controller.scene.scene.launch('VehicleEquipmentScene', {
            mode: 'equip',
            previousSceneKey: 'MenuScene' // 返回菜单场景
        });
    }
    
    /**
     * 处理ESC键
     */
    handleEscapeKey() {
        if (this.controller.state.currentMenu === 'main') {
            this.controller.scene.closeMenu();
        } else {
            this.showMenu('main', GameStateStore.getPlayer());
        }
    }
    
    /**
     * 处理上键
     */
    handleUpKey() {
        this.controller.state.selectedOption = Math.max(0, this.controller.state.selectedOption - 1);
        this.controller.operation.updateSelectedOption(GameStateStore.getPlayer());
    }
    
    /**
     * 处理下键
     */
    handleDownKey() {
        let options = this.controller.state.menuOptions[this.controller.state.currentMenu];
        
        if (this.controller.state.currentMenu !== 'main' && this.controller.state.currentMenu !== 'team') {
            const maxIndex = options.length;
            this.controller.state.selectedOption = Math.min(maxIndex, this.controller.state.selectedOption + 1);
            this.controller.operation.updateSelectedOption(GameStateStore.getPlayer());
        } else if (options && options.length > 0) {
            this.controller.state.selectedOption = Math.min(options.length - 1, this.controller.state.selectedOption + 1);
            this.controller.operation.updateSelectedOption(GameStateStore.getPlayer());
        }
    }
    
    /**
     * 处理分类向左切换
     */
    handleCategoryLeft() {
        const categories = this.controller.state.itemCategories;
        const currentIndex = categories.findIndex(cat => cat.key === this.controller.state.itemCategory);
        const newIndex = currentIndex > 0 ? currentIndex - 1 : categories.length - 1;
        this.controller.operation.switchItemCategory(categories[newIndex].key);
    }
    
    /**
     * 处理分类向右切换
     */
    handleCategoryRight() {
        const categories = this.controller.state.itemCategories;
        const currentIndex = categories.findIndex(cat => cat.key === this.controller.state.itemCategory);
        const newIndex = currentIndex < categories.length - 1 ? currentIndex + 1 : 0;
        this.controller.operation.switchItemCategory(categories[newIndex].key);
    }
}


